import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import ThemedSelect from "../shared/ThemedSelect";
import { useDirtyState } from "../../hooks/useDirtyState";
import { DirtyStateDialog } from "../shared/DirtyStateDialog";

const CreatePrediction = ({ form, setForm, editId, setEditId, setItems, handleInputChange, handleSubmit, comments }) => {
    const { markDirty, resetDirty, showDialog, confirmNavigation, cancelNavigation } = useDirtyState(form);

    const handleCommentChange = (selectedOption) => {
        const updated = {
            ...form,
            fk_comment_id: selectedOption ? selectedOption.value : "",
        };
        setForm(updated);
        markDirty(updated);
    };

    const handleLocalInputChange = (e) => {
        const { name, value } = e.target;
        handleInputChange(e);
        const updated = { ...form, [name]: value };
        markDirty(updated);
    };

    const commentOptions = comments.map(comment => ({
        value: comment.comment_id,
        label: `${comment.comment_id} - ${comment.comment_text}`,
    }));

    const selectedComment = comments.find(c => String(c.comment_id) === String(form.fk_comment_id));

    return (
        <>
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>{editId ? "Modifier la prédiction" : "Ajouter une prédiction"}</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); resetDirty(); }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-predicted-direction">Direction prédite</Label>
                                <Input
                                    id="prediction-predicted-direction"
                                    type="text"
                                    name="predicted_direction"
                                    value={form.predicted_direction}
                                    onChange={handleLocalInputChange}
                                    placeholder="Direction prédite"
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-predicted-pct">Variation prédite (%)</Label>
                                <Input
                                    id="prediction-predicted-pct"
                                    type="number"
                                    step="0.01"
                                    name="predicted_percentage_change"
                                    value={form.predicted_percentage_change}
                                    onChange={handleLocalInputChange}
                                    placeholder="Variation prédite (%)"
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-predicted-timeframe">Horizon</Label>
                                <Input
                                    id="prediction-predicted-timeframe"
                                    type="text"
                                    name="predicted_time_frame"
                                    value={form.predicted_time_frame}
                                    onChange={handleLocalInputChange}
                                    placeholder="Horizon"
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-actual-direction">Direction réelle</Label>
                                <Input
                                    id="prediction-actual-direction"
                                    type="text"
                                    name="actual_direction"
                                    value={form.actual_direction}
                                    onChange={handleLocalInputChange}
                                    placeholder="Direction réelle"
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-actual-pct">Variation réelle (%)</Label>
                                <Input
                                    id="prediction-actual-pct"
                                    type="number"
                                    step="0.01"
                                    name="actual_percentage_change"
                                    value={form.actual_percentage_change}
                                    onChange={handleLocalInputChange}
                                    placeholder="Variation réelle (%)"
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-evaluated-at">Date d'évaluation</Label>
                                <Input
                                    id="prediction-evaluated-at"
                                    type="datetime-local"
                                    name="evaluated_at"
                                    value={
                                        form.evaluated_at
                                            ? new Date(form.evaluated_at).toISOString().slice(0, 16)
                                            : ""
                                    }
                                    onChange={handleLocalInputChange}
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-score">Score</Label>
                                <Input
                                    id="prediction-score"
                                    type="number"
                                    name="score"
                                    value={form.score}
                                    onChange={handleLocalInputChange}
                                    placeholder="Score"
                                />
                            </div>
                        </div>

                        {/* Read-only derived fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-readonly-stock">Symbole de l'action</Label>
                                <Input
                                    id="prediction-readonly-stock"
                                    type="text"
                                    readOnly
                                    disabled
                                    value={selectedComment ? selectedComment.fk_stock_symbol : ''}
                                    placeholder="—"
                                />
                            </div>
                            <div className="grid w-full gap-1.5">
                                <Label htmlFor="prediction-readonly-predictor">Prédicteur</Label>
                                <Input
                                    id="prediction-readonly-predictor"
                                    type="text"
                                    readOnly
                                    disabled
                                    value={selectedComment ? selectedComment.predictorname : ''}
                                    placeholder="—"
                                />
                            </div>
                        </div>

                        <div className="grid w-full gap-1.5 mt-4">
                            <Label htmlFor="prediction-comment">Commentaire</Label>
                            <ThemedSelect
                                id="prediction-comment"
                                options={commentOptions}
                                value={commentOptions.find(option => String(option.value) === String(form.fk_comment_id)) || null}
                                onChange={handleCommentChange}
                                placeholder="Sélectionner un commentaire"
                                isClearable
                            />
                        </div>

                        <Button type="submit" className="mt-4">
                            {editId ? "Enregistrer" : "Ajouter"}
                        </Button>
                    </form>
                </CardContent>
            </Card>
            <DirtyStateDialog open={showDialog} onConfirm={confirmNavigation} onCancel={cancelNavigation} />
        </>
    );
};

export default CreatePrediction;
